import {
  Button,
  Typography,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import ResponsiveDrawer from "../../components/ClippedResponsiveDrawer";
import { AxiosRequestConfig } from "axios";
import React, { useEffect, useState } from "react";
import { AXIOS_INSTANCE, BASE_URL } from "../../App";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import TableAbstraction from "../../componentUtils/TableAbstraction";
import DateRangeHeader, { tableHeightReducePx } from "./DateRangeHeader";

interface ReportingState {
  isLoaded: boolean;
  data: ReportingRow[];
}

interface ReportingRow {
  salesOffice: string;
  route: string;
  loadDate: string;
  planned: number;
  executed: number;
  visitedCustomers: string;
  missedCustomers: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    rootContainer: {
      [theme.breakpoints.down("md")]: {
        padding: 0,
        paddingTop: theme.spacing(1),
      },
    },
    tokenBlock: {
      width: "500px",
      overflowWrap: "break-word",
    },
    typography: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
  })
);

async function getReportingData(salesOrg: string, deliveringPlants: string[], dateFrom: string, dateTo: string) {
  const configReporting: AxiosRequestConfig = {
    method: "POST",
    params: {
      clientId: "100",
      salesOrg: salesOrg,
    },
    data: {
      deliveringPlants: deliveringPlants,
      dateFrom: dateFrom,
      dateTo: dateTo,
    },
    url: `${BASE_URL}/api/reporting/missedstop`,
  };

  return await AXIOS_INSTANCE(configReporting).then((res: any) => res.data);
}

export default function ConfigurationReporting() {
  const classes = useStyles();
  const [state, setState] = useState<ReportingState>({
    isLoaded: false,
    data: [],
  });
  const [backdropLoader, setBackdropLoader] = React.useState<boolean>(false);

  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");

  const [handleAlert, setHandleAlert] = useState(false);
  const [alert, setAlert] = useState("");

  const onSelectionChange = (deliveringPlants: string[], dateFrom: string, dateTo: string) => {
    setBackdropLoader(true);
    const sOrg = salesOrg.toString();
    if (deliveringPlants.length === 0) {
      setAlert("Please select at least one delivery plant.");
      setHandleAlert(true);
      setBackdropLoader(false);
      return;
    }
    if (!dateFrom || !dateTo) {
      setAlert("Please select both From Date and To Date.");
      setHandleAlert(true);
      setBackdropLoader(false);
      return;
    }

    if(new Date(dateFrom) > new Date(dateTo)){
      setAlert("From date can not be greater than To date.");
      setHandleAlert(true);
      setBackdropLoader(false);
      return;
    }

    getReportingData(sOrg, deliveringPlants, dateFrom, dateTo).then((v) => {
      setState({ isLoaded: true, data: v });
      setBackdropLoader(false);
    });
  };

  return (
      <ResponsiveDrawer title="Reporting">
        <div className={classes.rootContainer}>
          <DateRangeHeader onSelectionChange={onSelectionChange} />
          <TableAbstraction
            settingsKey="RPT_MISS"
            options={{
              serverSide: false,
              download: false,
              responsive: "vertical",
              selectableRows: "none",
              rowsPerPage: 100,
              tableHeightReducePx,
              sortOrder: [{ name: "loadDate", direction: "asc" }],
            }}
            title="Missed Stops Overview"
            columns={[
              {
                label: "Sales Office",
                name: "salesOffice",
              },
              {
                label: "Delivery Plant",
                name: "deliveringPlant",
              },
              {
                label: "Route",
                name: "route",
              },
              {
                label: "Load Date",
                name: "loadDate",
              },
              {
                label: "Scheduled Stops",
                name: "planned",
              },
              {
                label: "Executed Stops",
                name: "executed",
              },
              {
                label: "Missed Stops",
                name: "missedStops",
                options: {
                  searchable: false,
                  filter: false,
                  renderCell: (params) => {
                    let item = params.row;
                    if (item) {
                      var missed = item.planned - item.executed;
                      return <Typography>{missed}</Typography>;
                    }
                  },
                },
              },
              {
                label: "Unserviced Customers",
                name: "missedCustomers",
              },
              {
                label: "Serviced Customers",
                name: "visitedCustomers",
              },
            ]}
            data={state.data}
          />
        </div>
        <Backdrop sx={{ color: "#fff", zIndex: (theme: any) => theme.zIndex.drawer + 1 }} open={backdropLoader}>
          <CircularProgress color="primary" />
        </Backdrop>
        {handleAlert ? <Dialog
          open={handleAlert}
          onClose={() => {
            setHandleAlert(false);
            setAlert("");
          }}
        >
        <DialogTitle>Missed Stop Alert</DialogTitle>
          <DialogContent>
            <DialogContentText>{alert}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAlert("");
                setHandleAlert(false);
              }}
              color="primary"
            >Ok</Button>
          </DialogActions>
        </Dialog>: null}
      </ResponsiveDrawer>
    );
}
